import { Spacer } from "@hackthenorth/north";
import { Card, Text } from "north.js";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { EventActivity, ShiftSignup } from "src/api/types.generated";
import { Header, PageWrapper } from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { useUserContext } from "src/shared/contexts/UserContext";
import { useDeviceSize } from "src/shared/hooks";

import VolunteerShiftModal from "../../shared/components/VolunteerShiftModal";
import {
  sumBufferedHours,
  TBufferShift,
  withBuffer,
} from "../../shared/utils/buffer";
import { useGetShiftSignupsSelfQuery } from "../graphql/getShiftSignupsSelf.generated";

import CollapsibleShiftSection from "./CollapsibleShiftSection";
import ShiftCard from "./ShiftCard";

const VolunteerDashboard: React.FC = () => {
  const isTabletOrSmaller = useDeviceSize("tablet");

  const [selectedShift, setSelectedShift] = React.useState<number | null>(null);
  const [viewModal, setViewModal] = React.useState<boolean>(false);

  const handleShiftClick = (shift: any) => {
    setSelectedShift(shift.shift_id);
    setViewModal(true);
  };

  const [upcomingShifts, setUpcomingShifts] = useState<
    (Pick<ShiftSignup, "shift_id" | "checked_in" | "event_attendee_id"> & {
      shift: Pick<
        EventActivity,
        "start_time" | "end_time" | "name" | "location"
      >;
    })[]
  >();
  const [completedShifts, setCompletedShifts] = useState<
    (Pick<ShiftSignup, "shift_id" | "checked_in" | "event_attendee_id"> & {
      shift: Pick<
        EventActivity,
        "start_time" | "end_time" | "name" | "location"
      >;
    })[]
  >();

  const { info } = useUserContext();
  const userName = info?.name.split(" ")[0] ?? "Volunteer";

  const { data: shiftSignupsData, loading: fetchingShiftSignupsData } =
    useGetShiftSignupsSelfQuery({
      variables: {
        event_id: EVENT_ID,
      },
    });

  const allBufferShifts: TBufferShift[] = useMemo(
    () =>
      (shiftSignupsData?.myVolunteerShiftSignups ?? []).map((signup) => ({
        shiftId: signup.shift_id,
        start: new Date(signup.shift.start_time),
        end: new Date(signup.shift.end_time),
        checkedIn: signup.checked_in,
      })),
    [shiftSignupsData]
  );

  const completedHours = useMemo(
    () =>
      sumBufferedHours(allBufferShifts, {
        checkedInOnly: true,
        pastOnly: true,
      }),
    [allBufferShifts]
  );

  const bufferedById = useMemo(() => {
    const map = new Map<number, ReturnType<typeof withBuffer>[number]>();
    withBuffer(allBufferShifts).forEach((shift) => {
      map.set(shift.shiftId, shift);
    });
    return map;
  }, [allBufferShifts]);

  useEffect(() => {
    if (shiftSignupsData && !fetchingShiftSignupsData) {
      const currentTime = new Date().toISOString();
      const tempUpcomingShifts =
        shiftSignupsData.myVolunteerShiftSignups.filter(
          (shift) => shift.shift.end_time > currentTime
        );
      const tempCompletedShifts =
        shiftSignupsData.myVolunteerShiftSignups.filter(
          (shift) => shift.shift.end_time <= currentTime
        );
      setUpcomingShifts(tempUpcomingShifts);
      setCompletedShifts(tempCompletedShifts);
    }
  }, [fetchingShiftSignupsData, shiftSignupsData]);

  if (!upcomingShifts || !completedShifts) return null;

  const roundedCompletedHours = completedHours;

  return (
    <>
      {selectedShift && (
        <VolunteerShiftModal
          shiftId={selectedShift}
          open={viewModal}
          setOpen={setViewModal}
        />
      )}
      <Header
        title="Volunteer Dashboard"
        subtitle="View and manage my shifts"
      />
      <PageWrapper>
        <HeaderText>Hi, {userName}!</HeaderText>
        <CardContainer>
          {isTabletOrSmaller ? (
            <StyledCardMobileContainer>
              <StyledCardMobile>
                <StyleCardMobileTitle>UPCOMING</StyleCardMobileTitle>
                <StyledCardMobileFlex>
                  <Text type="h1">{upcomingShifts.length}</Text>{" "}
                  <StyleCardMobileSubtitle>shifts</StyleCardMobileSubtitle>
                </StyledCardMobileFlex>
              </StyledCardMobile>
              <StyledCardMobile>
                <StyleCardMobileTitle>COMPLETED</StyleCardMobileTitle>
                <StyledCardMobileFlex>
                  <Text type="h1">{completedShifts.length}</Text>{" "}
                  <StyleCardMobileSubtitle>shifts</StyleCardMobileSubtitle>
                </StyledCardMobileFlex>
              </StyledCardMobile>
              <StyledCardMobile>
                <StyleCardMobileTitle>HOURS COLLECTED</StyleCardMobileTitle>
                <StyledCardMobileFlex>
                  <Text type="h1">{roundedCompletedHours}</Text>{" "}
                  <StyleCardMobileSubtitle>hours</StyleCardMobileSubtitle>
                </StyledCardMobileFlex>
              </StyledCardMobile>
            </StyledCardMobileContainer>
          ) : (
            <>
              <StyledCard>
                <LargeBodyBold>{upcomingShifts.length}</LargeBodyBold>
                <StyledH3>upcoming shifts</StyledH3>
              </StyledCard>
              <StyledCard>
                <LargeBodyBold>{completedShifts.length}</LargeBodyBold>
                <StyledH3>completed shifts</StyledH3>
              </StyledCard>
              <StyledCard>
                <LargeBodyBold>{roundedCompletedHours}</LargeBodyBold>
                <StyledH3>hours collected</StyledH3>
              </StyledCard>
            </>
          )}
        </CardContainer>
        <Spacer height={40} />
        <div className="text-text-tertiary mb-4">
          All times are in Eastern Time (EDT)
        </div>
        <CollapsibleShiftSection
          title="Upcoming Shifts"
          count={upcomingShifts.length}
          defaultOpen
          emptyMessage="You have no upcoming shifts"
        >
          <CardsContainer>
            {upcomingShifts.map((shift) => {
              const buffered = bufferedById.get(shift.shift_id);
              return (
                <ShiftCard
                  key={shift.shift_id}
                  shiftName={shift.shift.name}
                  startTime={shift.shift.start_time}
                  endTime={shift.shift.end_time}
                  effectiveEndTime={
                    buffered?.hasBuffer
                      ? buffered.effectiveEnd.toISOString()
                      : undefined
                  }
                  shiftLocation={shift.shift.location!}
                  onClick={() => handleShiftClick(shift)}
                  checkedIn={shift.checked_in}
                  isVolunteer
                />
              );
            })}
          </CardsContainer>
        </CollapsibleShiftSection>
        <Spacer height={40} />
        <CollapsibleShiftSection
          title="Completed Shifts"
          count={completedShifts.length}
          defaultOpen
          emptyMessage="You have no completed shifts"
        >
          <CardsContainer>
            {completedShifts.map((shift) => {
              const buffered = bufferedById.get(shift.shift_id);
              return (
                <ShiftCard
                  key={shift.shift_id}
                  shiftName={shift.shift.name}
                  startTime={shift.shift.start_time}
                  endTime={shift.shift.end_time}
                  effectiveEndTime={
                    buffered?.hasBuffer
                      ? buffered.effectiveEnd.toISOString()
                      : undefined
                  }
                  shiftLocation={shift.shift.location!}
                  onClick={() => handleShiftClick(shift)}
                  checkedIn={shift.checked_in}
                  isVolunteer
                />
              );
            })}
          </CardsContainer>
        </CollapsibleShiftSection>
      </PageWrapper>
    </>
  );
};

const HeaderText = styled.h1`
  color: #000;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 34px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
  margin-bottom: 24px;
`;

const CardContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1085px;
  justify-content: space-between;
  align-items: stretch;
`;

const StyledCard = styled(Card)`
  display: flex;
  width: 32%;
  padding: 16px 24px 32px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex-shrink: 0;
`;

const StyledCardMobile = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCardMobileFlex = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 5px;
`;

const StyledCardMobileContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const StyleCardMobileSubtitle = styled(Text)`
  font-weight: 500 !important;
  font-size: 13px !important;

  color: ${({ theme }) => theme.globalConstants.color.textSecondary} !important;
`;

const StyleCardMobileTitle = styled(Text)`
  font-weight: 500 !important;
  font-size: 13px !important;

  color: ${({ theme }) => theme.globalConstants.color.textBody} !important;
`;

const LargeBodyBold = styled.p`
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-size: 64px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
`;

const StyledH3 = styled.h3`
  overflow: hidden;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  text-overflow: ellipsis;

  /* North/Text/H3 */
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 20px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
`;

const CardsContainer = styled.div`
  padding: 16px 16px 16px 0px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

export default VolunteerDashboard;
